<template>
  <div class="auto-map-modal" v-if="visible">
    <div class="modal-overlay" @click="close"></div>
    <div class="modal-body">
      <div class="modal-header">
        <h2>{{ $t('auto_map') }}</h2>
        <p class="modal-subtitle">{{ $t('auto_map_description') }}</p>
        <button class="btn-close" @click="close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"/>
          </svg>
        </button>
      </div>

      <div class="modal-content">
        <!-- Step 1: File Upload -->
        <div class="step-section" v-if="currentStep === 1">
          <div
            class="upload-zone"
            :class="{ 'is-dragover': isDragover, 'has-file': uploadedFile }"
            @dragover.prevent="isDragover = true"
            @dragleave="isDragover = false"
            @drop.prevent="handleDrop"
          >
            <input
              type="file"
              ref="fileInput"
              accept=".xlsx,.csv,.txt"
              @change="handleFileSelect"
              class="file-input"
            />
            <div class="upload-content" v-if="!uploadedFile">
              <svg class="upload-icon" width="48" height="48" viewBox="0 0 48 48" fill="currentColor">
                <path d="M24 4L14 14h7v14h6V14h7L24 4zm-20 36v4h40v-4H4z"/>
              </svg>
              <p class="upload-text">{{ $t('upload_sample_file') }}</p>
              <p class="upload-hint">XLSX, CSV, TXT</p>
              <button class="btn-browse" @click="$refs.fileInput.click()">
                {{ $t('browse') }}
              </button>
            </div>
            <div class="uploaded-file" v-else>
              <svg class="file-icon" width="32" height="32" viewBox="0 0 32 32" fill="currentColor">
                <path d="M6 2v28h20V10l-8-8H6zm12 2l6 6h-6V4zm-8 14h12v2H10v-2zm0 4h12v2H10v-2zm0 4h8v2h-8v-2z"/>
              </svg>
              <div class="file-info">
                <span class="file-name">{{ uploadedFile.name }}</span>
                <span class="file-size">{{ formatFileSize(uploadedFile.size) }}</span>
              </div>
              <button class="btn-remove" @click="removeFile">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 8.586L14.293 4.293 15.707 5.707 11.414 10l4.293 4.293-1.414 1.414L10 11.414l-4.293 4.293-1.414-1.414L8.586 10 4.293 5.707 5.707 4.293 10 8.586z"/>
                </svg>
              </button>
            </div>
          </div>

          <div class="template-type-selector" v-if="showTemplateTypeSelector">
            <label class="selector-label">{{ $t('template_type') }}</label>
            <select v-model="selectedTemplateType" class="type-select">
              <option value="shipment_fields">{{ $t('shipment') }}</option>
              <option value="customer_fields">{{ $t('customer') }}</option>
              <option value="item_fields">{{ $t('item') }}</option>
              <option value="ftp_shipment_input_fields">{{ $t('ftp_input') }}</option>
            </select>
          </div>

          <button
            class="btn-generate"
            :disabled="!uploadedFile || isLoading"
            @click="generateSuggestions"
          >
            <span class="btn-icon" :class="{ 'is-loading': isLoading }">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 0L12.5 7.5L20 10L12.5 12.5L10 20L7.5 12.5L0 10L7.5 7.5L10 0Z"/>
              </svg>
            </span>
            <span>{{ isLoading ? $t('analyzing') : $t('generate_suggestions') }}</span>
          </button>
        </div>

        <!-- Step 2: Preview & Suggestions -->
        <div class="step-section" v-if="currentStep === 2">
          <!-- File Preview -->
          <div class="preview-section" v-if="preview">
            <h3>{{ $t('preview') }}</h3>
            <div class="preview-table-wrapper">
              <table class="preview-table">
                <thead>
                  <tr>
                    <th v-for="(header, index) in preview.headers" :key="index">
                      {{ header || `Column ${index + 1}` }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rowIndex) in preview.sample_rows" :key="rowIndex">
                    <td v-for="(cell, cellIndex) in row" :key="cellIndex">
                      {{ cell }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Suggestions List -->
          <div class="suggestions-section" v-if="suggestions.length > 0">
            <div class="suggestions-header">
              <h3>{{ $t('suggestions') }} ({{ suggestions.length }})</h3>
              <div class="suggestions-actions">
                <button class="btn-select-all" @click="selectAllSuggestions">
                  {{ allSelected ? $t('deselect_all') : $t('select_all') }}
                </button>
              </div>
            </div>
            <div class="suggestions-list">
              <div
                v-for="(suggestion, index) in suggestions"
                :key="index"
                class="suggestion-item"
                :class="{ 'is-selected': selectedSuggestions.includes(index) }"
                @click="toggleSuggestion(index)"
              >
                <div class="suggestion-checkbox">
                  <input
                    type="checkbox"
                    :checked="selectedSuggestions.includes(index)"
                    @click.stop
                    @change="toggleSuggestion(index)"
                  />
                </div>
                <div class="suggestion-source">
                  <span class="field-label">{{ suggestion.file_column }}</span>
                </div>
                <div class="suggestion-arrow">
                  <svg width="24" height="12" viewBox="0 0 24 12" fill="none">
                    <path d="M0 6h20M18 2l4 4-4 4" stroke="#59D076" stroke-width="2"/>
                  </svg>
                </div>
                <div class="suggestion-target">
                  <span class="field-label">{{ suggestion.field_name }}</span>
                </div>
                <div class="suggestion-confidence">
                  <span
                    class="confidence-badge"
                    :class="getConfidenceClass(suggestion.confidence)"
                  >
                    {{ Math.round(suggestion.confidence * 100) }}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- No Suggestions -->
          <div class="no-suggestions" v-else-if="!isLoading">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="currentColor">
              <path d="M24 4C12.95 4 4 12.95 4 24s8.95 20 20 20 20-8.95 20-20S35.05 4 24 4zm2 30h-4v-4h4v4zm0-8h-4V14h4v12z"/>
            </svg>
            <p>{{ $t('no_suggestions_found') }}</p>
            <button class="btn-retry" @click="currentStep = 1">
              {{ $t('try_again') }}
            </button>
          </div>
        </div>
      </div>

      <div class="modal-footer" v-if="currentStep === 2 && suggestions.length > 0">
        <button class="btn-back" @click="currentStep = 1">
          {{ $t('back') }}
        </button>
        <button
          class="btn-apply"
          :disabled="selectedSuggestions.length === 0"
          @click="applySuggestions"
        >
          {{ $t('apply_suggestions') }} ({{ selectedSuggestions.length }})
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'AutoMapModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    templateType: {
      type: String,
      default: 'shipment_fields'
    },
    showTemplateTypeSelector: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentStep: 1,
      isDragover: false,
      uploadedFile: null,
      selectedTemplateType: this.templateType,
      isLoading: false,
      preview: null,
      suggestions: [],
      selectedSuggestions: [],
      error: null
    };
  },
  computed: {
    allSelected() {
      return this.selectedSuggestions.length === this.suggestions.length && this.suggestions.length > 0;
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.reset();
      }
    },
    templateType(val) {
      this.selectedTemplateType = val;
    }
  },
  methods: {
    ...mapActions(['AI_SUGGEST_MAPPING']),

    reset() {
      this.currentStep = 1;
      this.uploadedFile = null;
      this.preview = null;
      this.suggestions = [];
      this.selectedSuggestions = [];
      this.error = null;
      this.isLoading = false;
    },

    close() {
      this.$emit('close');
    },

    handleDrop(event) {
      this.isDragover = false;
      const { files } = event.dataTransfer;
      if (files.length > 0) {
        this.setFile(files[0]);
      }
    },

    handleFileSelect(event) {
      const { files } = event.target;
      if (files.length > 0) {
        this.setFile(files[0]);
      }
    },

    setFile(file) {
      const ext = file.name.split('.').pop().toLowerCase();

      if (!['xlsx', 'csv', 'txt'].includes(ext)) {
        this.error = this.$t('invalid_file_type');
        return;
      }

      this.uploadedFile = file;
      this.error = null;
    },

    removeFile() {
      this.uploadedFile = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    formatFileSize(bytes) {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },

    async generateSuggestions() {
      if (!this.uploadedFile) return;

      this.isLoading = true;
      this.error = null;

      try {
        const formData = new FormData();
        formData.append('file', this.uploadedFile);
        formData.append('template_type', this.selectedTemplateType);

        const response = await this.AI_SUGGEST_MAPPING(formData);

        if (response && response.data) {
          this.preview = response.data.preview || null;
          this.suggestions = response.data.suggestions || [];
          // Auto-select high confidence suggestions
          this.selectedSuggestions = this.suggestions
            .map((s, i) => (s.confidence >= 0.7 ? i : -1))
            .filter(i => i !== -1);
          this.currentStep = 2;
        }
      } catch (error) {
        console.error('AI mapping error:', error);
        this.error = error.message || this.$t('ai_mapping_error');
      } finally {
        this.isLoading = false;
      }
    },

    toggleSuggestion(index) {
      const pos = this.selectedSuggestions.indexOf(index);
      if (pos === -1) {
        this.selectedSuggestions.push(index);
      } else {
        this.selectedSuggestions.splice(pos, 1);
      }
    },

    selectAllSuggestions() {
      if (this.allSelected) {
        this.selectedSuggestions = [];
      } else {
        this.selectedSuggestions = this.suggestions.map((_, i) => i);
      }
    },

    getConfidenceClass(confidence) {
      if (confidence >= 0.9) return 'confidence-high';
      if (confidence >= 0.7) return 'confidence-medium';
      return 'confidence-low';
    },

    applySuggestions() {
      const selectedMappings = this.selectedSuggestions
        .map(index => this.suggestions[index])
        .filter(Boolean);

      this.$emit('apply', selectedMappings);
      this.close();
    }
  }
};
</script>

<style lang="scss" scoped>
$color-brand: #FF9300;
$color-dark: #363D44;
$color-light: #989FA6;
$color-l-bg: #F2F4F6;
$color-bright: #FFF;
$color-green: #59D076;
$color-warning: #ed5656;
$color-border: #DBDFE3;

.auto-map-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
}

.modal-body {
  position: relative;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  background: $color-bright;
  border-radius: 8px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  padding: 24px;
  border-bottom: 1px solid $color-border;
  position: relative;

  h2 {
    margin: 0 0 8px 0;
    font-size: 20px;
    font-weight: 600;
    color: $color-dark;
  }

  .modal-subtitle {
    margin: 0;
    font-size: 14px;
    color: $color-light;
  }

  .btn-close {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 36px;
    height: 36px;
    border: none;
    background: transparent;
    color: $color-light;
    cursor: pointer;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;

    &:hover {
      background: $color-l-bg;
      color: $color-dark;
    }
  }
}

.modal-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid $color-border;
  display: flex;
  justify-content: space-between;
  gap: 12px;

  .btn-back {
    padding: 12px 24px;
    border: 1px solid $color-border;
    border-radius: 4px;
    background: transparent;
    color: $color-dark;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: $color-l-bg;
    }
  }

  .btn-apply {
    padding: 12px 24px;
    border: none;
    border-radius: 4px;
    background: $color-brand;
    color: $color-bright;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(:disabled) {
      background: darken($color-brand, 10%);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.upload-zone {
  border: 2px dashed $color-border;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  transition: all 0.3s;
  position: relative;

  &.is-dragover {
    border-color: $color-brand;
    background: rgba($color-brand, 0.05);
  }

  &.has-file {
    border-style: solid;
    border-color: $color-green;
    background: rgba($color-green, 0.05);
    padding: 20px;
  }

  .file-input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }
}

.upload-content {
  .upload-icon {
    color: $color-light;
    margin-bottom: 16px;
  }

  .upload-text {
    font-size: 16px;
    color: $color-dark;
    margin: 0 0 4px 0;
  }

  .upload-hint {
    font-size: 13px;
    color: $color-light;
    margin: 0 0 16px 0;
  }

  .btn-browse {
    padding: 10px 24px;
    border: 1px solid $color-brand;
    border-radius: 4px;
    background: transparent;
    color: $color-brand;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: $color-brand;
      color: $color-bright;
    }
  }
}

.uploaded-file {
  display: flex;
  align-items: center;
  gap: 16px;

  .file-icon {
    color: $color-green;
  }

  .file-info {
    flex: 1;
    text-align: left;

    .file-name {
      display: block;
      font-size: 14px;
      font-weight: 500;
      color: $color-dark;
    }

    .file-size {
      display: block;
      font-size: 12px;
      color: $color-light;
      margin-top: 2px;
    }
  }

  .btn-remove {
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: $color-light;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;

    &:hover {
      background: rgba($color-warning, 0.1);
      color: $color-warning;
    }
  }
}

.template-type-selector {
  margin-top: 20px;

  .selector-label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: $color-dark;
    margin-bottom: 8px;
  }

  .type-select {
    width: 100%;
    padding: 10px 14px;
    border: 1px solid $color-border;
    border-radius: 4px;
    font-size: 14px;
    background: $color-bright;
    cursor: pointer;

    &:focus {
      outline: none;
      border-color: $color-brand;
    }
  }
}

.btn-generate {
  margin-top: 24px;
  width: 100%;
  padding: 14px 24px;
  border: none;
  border-radius: 4px;
  background: linear-gradient(135deg, $color-brand 0%, darken($color-brand, 10%) 100%);
  color: $color-bright;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba($color-brand, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  .btn-icon {
    display: flex;
    align-items: center;
    justify-content: center;

    &.is-loading {
      animation: pulse 1.5s ease-in-out infinite;
    }
  }
}

.preview-section {
  margin-bottom: 24px;

  h3 {
    font-size: 14px;
    font-weight: 600;
    color: $color-dark;
    margin: 0 0 12px 0;
  }
}

.preview-table-wrapper {
  overflow-x: auto;
  border: 1px solid $color-border;
  border-radius: 4px;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th, td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid $color-border;
    white-space: nowrap;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  th {
    background: $color-l-bg;
    font-weight: 600;
    color: $color-dark;
  }

  td {
    color: $color-light;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.suggestions-section {
  .suggestions-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    h3 {
      font-size: 14px;
      font-weight: 600;
      color: $color-dark;
      margin: 0;
    }

    .btn-select-all {
      padding: 6px 12px;
      border: 1px solid $color-border;
      border-radius: 4px;
      background: transparent;
      color: $color-dark;
      font-size: 12px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: $color-l-bg;
      }
    }
  }
}

.suggestions-list {
  border: 1px solid $color-border;
  border-radius: 4px;
  overflow: hidden;
}

.suggestion-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid $color-border;
  cursor: pointer;
  transition: all 0.15s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: $color-l-bg;
  }

  &.is-selected {
    background: rgba($color-green, 0.08);
  }

  .suggestion-checkbox {
    margin-right: 12px;

    input[type="checkbox"] {
      width: 18px;
      height: 18px;
      cursor: pointer;
    }
  }

  .suggestion-source,
  .suggestion-target {
    flex: 1;
    min-width: 0;

    .field-label {
      font-size: 13px;
      color: $color-dark;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
    }
  }

  .suggestion-arrow {
    padding: 0 16px;
    flex-shrink: 0;
  }

  .suggestion-confidence {
    margin-left: 12px;
    flex-shrink: 0;

    .confidence-badge {
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 600;

      &.confidence-high {
        background: rgba($color-green, 0.1);
        color: $color-green;
      }

      &.confidence-medium {
        background: rgba($color-brand, 0.1);
        color: $color-brand;
      }

      &.confidence-low {
        background: rgba($color-light, 0.2);
        color: $color-light;
      }
    }
  }
}

.no-suggestions {
  text-align: center;
  padding: 40px 20px;

  svg {
    color: $color-light;
    margin-bottom: 16px;
  }

  p {
    font-size: 14px;
    color: $color-light;
    margin: 0 0 16px 0;
  }

  .btn-retry {
    padding: 10px 20px;
    border: 1px solid $color-brand;
    border-radius: 4px;
    background: transparent;
    color: $color-brand;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: $color-brand;
      color: $color-bright;
    }
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(0.9);
  }
}
</style>
